import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { LenticMark, DemoFooter } from '../../components/demo/DemoChrome';
import { ASSESSMENT_URL, DEMO_LIBRARY, samplePosting } from '../../data/demo';
export function Intake() {
  const {
    postingUrl,
    setPostingUrl,
    postingText,
    setPostingText,
    useSamplePosting,
    candidateCount,
    setCandidateCount,
    setMode
  } = useDemo();
  const [pasteMode, setPasteMode] = useState<'url' | 'text'>('url');
  const [collapsing, setCollapsing] = useState(false);
  const navigate = useNavigate();
  const filled = pasteMode === 'url' ? postingUrl.trim().length > 0 : postingText.trim().length > 0;
  const submit = () => {
    if (!filled || collapsing) return;
    setMode(candidateCount === 1 ? 'single' : 'cohort');
    // The field collapses upward into the source chip that opens the next screen.
    setCollapsing(true);
    window.setTimeout(() => navigate('/demo/analysis'), 220);
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-625" data-mp="11r5xjv0n39je">
      <div className="px-8 pt-8" data-id="element-626" data-mp="16p7cc3r18mp2">
        <LenticMark data-id="element-627" data-mp="16m31womz4upy" />
      </div>

      <main className="flex flex-1 justify-center px-8 py-20" data-id="element-628" data-mp="1bn0nlb7706w7">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="w-full max-w-[640px]" data-id="element-629" data-mp="1bl68idilr901">
          <p className="eyebrow text-brand" data-id="element-630" data-mp="nxoai08qveom">New assessment</p>
          <h1 className="mt-4 text-[42px] font-semibold leading-[1.1] tracking-tight text-ink" data-id="element-631" data-mp="nwd9u9alin1q">
            Start from the role.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-muted" data-id="element-632" data-mp="3reod4ljvmx4z">
            Paste the posting. Lentic builds the assessment from its gated scenario library — and
            shows you every check it passes on the way.
          </p>

          <motion.div initial={false} animate={{
          height: collapsing ? 0 : 'auto',
          opacity: collapsing ? 0 : 1,
          y: collapsing ? -12 : 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-12 overflow-hidden" data-id="element-633" data-mp="3obh66eklz6mw">
            <label htmlFor={pasteMode === 'url' ? 'posting-url' : 'posting-text'} className="eyebrow block text-muted" data-id="element-634" data-mp="35dxx5ay5ho0h">
              Posting
            </label>
            {pasteMode === 'url' ? <input id="posting-url" value={postingUrl} onChange={e => setPostingUrl(e.target.value)} placeholder={samplePosting.url} className="mt-3 w-full border-b border-rule bg-transparent pb-3 font-mono-num text-[14px] text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-635" data-mp="3fd56zo5of0iu" /> : <textarea id="posting-text" rows={7} value={postingText} onChange={e => setPostingText(e.target.value)} placeholder="Paste the full text of the posting." className="mt-3 w-full rounded-sm border border-rule bg-white p-4 text-[14px] leading-relaxed text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-636" data-mp="27iv9mrekwon7" />}

            <div className="mt-3 flex items-center justify-between gap-4 text-[12px]" data-id="element-637" data-mp="2vaisqn5lmvex">
              <button type="button" onClick={() => setPasteMode(pasteMode === 'url' ? 'text' : 'url')} className="text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink" data-id="element-638" data-mp="2hiugmjvco6xy">
                {pasteMode === 'url' ? 'paste the text instead' : 'use a URL instead'}
              </button>
              <button type="button" onClick={() => {
              setPasteMode('url');
              useSamplePosting();
            }} className="font-medium text-brand underline decoration-brand/40 underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-639" data-mp="1qbx98yorj4n7">
                use a sample posting
              </button>
            </div>
          </motion.div>

          <div className="mt-14" data-id="element-640" data-mp="294rgt7k23uhe">
            <p className="eyebrow text-muted" data-id="element-641" data-mp="251bognf0sgai">Candidates</p>
            <div className="mt-3 flex items-center gap-5" data-id="element-642" data-mp="251v2ht1jukj9">
              <button type="button" aria-label="Fewer candidates" onClick={() => setCandidateCount(Math.max(1, candidateCount - 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-643" data-mp="209jx4ao1i6jb">
                <MinusIcon className="h-4 w-4" data-id="element-644" data-mp="2ny350p36ghav" />
              </button>
              <span className="font-mono-num text-[40px] font-medium leading-none text-ink" data-id="element-645" data-mp="2vcwwasb26w4f">
                {candidateCount}
              </span>
              <button type="button" aria-label="More candidates" onClick={() => setCandidateCount(Math.min(12, candidateCount + 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-646" data-mp="359i57oq78rdj">
                <PlusIcon className="h-4 w-4" data-id="element-647" data-mp="3j76q2wk9nyil" />
              </button>
            </div>
            <div className="relative mt-4 min-h-[42px] max-w-md" data-id="element-648" data-mp="3o09ih1db51x7">
              <AnimatePresence mode="wait" initial={false} data-id="element-649" data-mp="3sz3zpyvmmif7">
                <motion.p key={candidateCount === 1 ? 'single' : 'cohort'} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="text-[13.5px] leading-relaxed text-muted" data-id="element-650" data-mp="3t0nfzpha8tgz">
                  {candidateCount === 1 ? 'One candidate — a tailored deep probe. Results won’t be comparable to anyone else’s, by design.' : `${candidateCount} candidates — one identical instrument for all. That’s what makes the results comparable, and the comparison defensible.`}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          <div className="mt-14 flex justify-end" data-id="element-651" data-mp="2go9usbrurbb7">
            <button type="button" onClick={submit} disabled={!filled} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-muted/25 disabled:text-muted" data-id="element-652" data-mp="2um6rr9a7xjqe">
              Analyse the role
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-653" data-mp="36wpsq8nu2vtz" />
            </button>
          </div>

          <p className="eyebrow mt-14 normal-case tracking-[0.06em] text-muted/70" data-id="element-654" data-mp="2y4e4mtg6zkh6">
            FIN library · {DEMO_LIBRARY} · last gated against 3 frontier models
          </p>
          <a href={ASSESSMENT_URL} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[13px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-655" data-mp="2o5oiupl6dfvn">
            Open the live assessment ↗
          </a>
        </motion.div>
      </main>

      <DemoFooter data-id="element-656" data-mp="2j8odkgw5gh5l" />
    </div>;
}